/**
 * useRailSidecar — fetch / resolve a guiding-rail calibration sidecar.
 *
 * Lookup order matches the other sidecar hooks: built-in compiled data →
 * HTTP fetch of `<assetsBaseUrl>/<id>.rail.meta.json` → identity correction.
 */

import { useEffect, useState } from 'react';
import {
  SidecarLoader,
  type RailSidecar,
  type OriginCorrection,
  DEFAULT_ORIGIN_CORRECTION,
  railSidecarFilename,
} from '../../assets/SidecarLoader.js';
import { BUILTIN_RAIL_SIDECARS } from '../../assets/builtinSidecars.js';
import { useSidecarSource } from '../../assets/SidecarSourceContext.js';
import { composeAssetUrl } from '../../assets/AssetLoader.js';
import type { RailType3D } from '../../types.js';

const SHARED = new SidecarLoader();

export function useRailSidecar(
  railType: RailType3D,
  assetsBaseUrl: string,
): { sidecar: RailSidecar | undefined; correction: OriginCorrection } {
  const { useBuiltins } = useSidecarSource();
  const builtin = useBuiltins ? BUILTIN_RAIL_SIDECARS[railType] : undefined;
  const [sidecar, setSidecar] = useState<RailSidecar | undefined>(builtin);

  useEffect(() => {
    if (builtin) {
      setSidecar(builtin);
      return;
    }
    let alive = true;
    const url = composeAssetUrl(assetsBaseUrl, railSidecarFilename(railType));
    SHARED.fetchRail(url)
      .then((s) => {
        if (alive) setSidecar(s);
      })
      .catch(() => {
        if (alive) setSidecar(undefined);
      });
    return () => {
      alive = false;
    };
  }, [railType, assetsBaseUrl, builtin]);

  return {
    sidecar,
    correction: sidecar?.originCorrection ?? DEFAULT_ORIGIN_CORRECTION,
  };
}
